import { Fragment, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken, useProperty, RichTextContent } from '@vibesites/runtime/react';
import { getEligibleNavPages, hasInteractiveMapContent } from './_navShared';

const THANK_YOU_EXCLUDED_ALIASES = new Set<string>(['apply-now', 'resident-access']);

export function ThankYouContent() {
  // Mirror the Sitemap page's layout exactly: an editable intro line above the
  // same centered "pages in navigation" link list. The eligibility logic below
  // is shared with the header and the sitemap (see ./_navShared), so the three
  // never drift.
  // Virtual Tours is always listed here, whether or not the property has tour
  // data — the page's own visibility setting is the only gate.
  const property = useProperty() as Record<string, unknown>;

  // Same Interactive Map gate as the header, so this list doesn't surface a
  // page the nav hides. (The override token is header-scoped and read-only here.)
  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveLeasing = hasInteractiveMapContent(property, interactiveMapShowIfExists);

  const pages = useMemo(
    () =>
      getEligibleNavPages(true, hasInteractiveLeasing)
        .filter(([alias]) => !THANK_YOU_EXCLUDED_ALIASES.has(alias))
        .map(([, page]) => page),
    [hasInteractiveLeasing],
  );

  return (
    <section className="pt-8 md:pt-10 pb-12 md:pb-16 bg-[var(--vibe-color-background)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <RichTextContent
          path="thankyoucontent.intro"
          label="Intro Text"
          defaultValue="<p>A leasing professional will contact you shortly.</p>"
          as="div"
          className="text-center font-heading text-base md:text-lg font-normal text-[var(--vibe-color-primary)] mb-5 [&_p]:m-0"
        />
        {pages.map((page, index) => (
          <Fragment key={page.path}>
            {index > 0 && (
              <hr className="mx-auto w-12 border-t border-[var(--vibe-color-border)]" />
            )}
            <a
              href={page.path}
              className="block text-center font-heading text-lg md:text-2xl font-normal text-[var(--vibe-color-primary)] py-3 hover:opacity-80 transition-opacity"
            >
              {page.name}
            </a>
          </Fragment>
        ))}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ThankYouContent',
  displayName: 'Thank You Content',
  tokens: {
    'thankyoucontent.intro': {
      label: 'Intro Text',
      type: 'richtext',
      defaultValue: '<p>A leasing professional will contact you shortly.</p>',
    },
  },
};

(ThankYouContent as any).contentSchema = contentSchema;

export default ThankYouContent;
